import { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { PartnerAutocomplete } from "../components/PartnerAutocomplete";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import { toast } from "sonner";
import { Download, Upload, Trash2, Pencil, Check, X as XIcon, Plus, Search } from "lucide-react";
import { Input } from "../components/ui/input";

const CURRENCIES = ["USD", "EUR", "AED", "KWD", "INR", "USDT"];

function blankNew() {
    return {
        entity_id: "", segment: "Voice", partner_name: "",
        currency: "USD", debit_base: "0", credit_base: "0",
    };
}

export default function OpeningBalancesPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("opening_balances", "write");
    const { entities } = useAppCtx();
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState("");

    // Client-side substring filter on partner name. Reusing the same UX
    // pattern as Sales Recon / Purchase Invoices for consistency.
    const filteredRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => (r.partner_name || "").toLowerCase().includes(q));
    }, [rows, query]);
    const [entityId, setEntityId] = useState("");
    const [filterEntity, setFilterEntity] = useState("all");
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [newOpen, setNewOpen] = useState(false);
    const [newForm, setNewForm] = useState(blankNew());
    const fileRef = useRef();

    const load = async () => {
        const params = filterEntity !== "all" ? { entity_id: filterEntity } : {};
        const { data } = await api.get("/opening-balances", { params });
        setRows(data);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [filterEntity]);

    const downloadTemplate = async () => {
        const token = localStorage.getItem("erp_access_token");
        // Cache-busting: append a timestamp + send `cache: 'no-store'` so the
        // browser cannot serve a stale template from the previous schema.
        const res = await fetch(`${API}/opening-balances/template?t=${Date.now()}`, {
            credentials: "include",
            cache: "no-store",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "opening_balances_template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        // Entity is optional now — used as fallback when CSV omits Entity_Name column
        const url = entityId
            ? `/opening-balances/upload?entity_id=${entityId}`
            : `/opening-balances/upload`;
        try {
            const { data } = await api.post(url, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const skipped = (data.skipped || []).length;
            const ins = data.inserted || 0;
            const rep = data.replaced || 0;
            toast.success(`${ins} new + ${rep} replaced${skipped ? ` — ${skipped} skipped` : ""}`);
            if (skipped) {
                console.warn("Opening balance import skipped rows:", data.skipped);
                // Persistent warning toast (10s) so the user can copy the
                // exact reasons. Truncated list inside, full list in console.
                toast.warning(
                    `${skipped} row${skipped === 1 ? "" : "s"} skipped — open browser console for full list`,
                    {
                        description: data.skipped.slice(0, 5).join("\n") +
                                     (data.skipped.length > 5 ? `\n… +${data.skipped.length - 5} more` : ""),
                        duration: 10000,
                    },
                );
            }
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            e.target.value = "";
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete opening balance row?")) return;
        await api.delete(`/opening-balances/${id}`);
        await load();
    };

    const submitNew = async () => {
        if (!newForm.entity_id) return toast.error("Entity is required");
        if (!newForm.partner_name?.trim()) return toast.error("Partner is required");
        try {
            const { data } = await api.post("/opening-balances", {
                entity_id: newForm.entity_id,
                segment: newForm.segment,
                partner_name: newForm.partner_name.trim(),
                currency: newForm.currency,
                debit_base: parseFloat(newForm.debit_base) || 0,
                credit_base: parseFloat(newForm.credit_base) || 0,
            });
            toast.success(data.replaced ? "Existing opening balance replaced" : "Opening balance created");
            setNewOpen(false);
            setNewForm(blankNew());
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        }
    };

    const startEdit = (r) => {
        setEditId(r.id);
        setEditForm({
            entity_id: r.entity_id,
            segment: r.segment,
            partner_name: r.partner_name || "",
            currency: r.currency || "USD",
            debit_base: String(r.debit_base ?? 0),
            credit_base: String(r.credit_base ?? 0),
        });
    };
    const cancelEdit = () => { setEditId(null); setEditForm({}); };
    const saveEdit = async () => {
        if (!editForm.partner_name?.trim()) return toast.error("Partner is required");
        try {
            await api.put(`/opening-balances/${editId}`, {
                entity_id: editForm.entity_id,
                segment: editForm.segment,
                partner_name: editForm.partner_name.trim(),
                currency: editForm.currency,
                debit_base: parseFloat(editForm.debit_base) || 0,
                credit_base: parseFloat(editForm.credit_base) || 0,
            });
            toast.success("Opening balance updated");
            cancelEdit();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        }
    };

    const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";

    return (
        <div data-testid="opening-balances-page">
            <PageHeader
                title="Opening Balances"
                eyebrow="Data Migration"
                description="Bulk-load opening balances as of 1 May 2026 from CSV or add manually. Voice rows post in USD, SMS rows post in EUR."
                action={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={downloadTemplate} className="rounded-sm" data-testid="ob-template-btn">
                            <Download size={14} className="mr-1.5" /> Template (.csv)
                        </Button>
                        {canWrite && (
                            <Dialog open={newOpen} onOpenChange={(v) => { setNewOpen(v); if (!v) setNewForm(blankNew()); }}>
                                <DialogTrigger asChild>
                                    <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="ob-new-btn">
                                        <Plus size={14} className="mr-1.5"/>New row
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="rounded-sm max-w-xl" data-testid="ob-new-dialog">
                                    <DialogHeader><DialogTitle>Add opening balance</DialogTitle></DialogHeader>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <Field label="Entity">
                                            <Select value={newForm.entity_id} onValueChange={(v) => setNewForm({ ...newForm, entity_id: v })}>
                                                <SelectTrigger className="rounded-sm" data-testid="ob-new-entity"><SelectValue placeholder="Select entity"/></SelectTrigger>
                                                <SelectContent>
                                                    {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label="Segment">
                                            <Select value={newForm.segment} onValueChange={(v) => setNewForm({ ...newForm, segment: v })}>
                                                <SelectTrigger className="rounded-sm" data-testid="ob-new-segment"><SelectValue/></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Voice">Voice (USD base)</SelectItem>
                                                    <SelectItem value="SMS">SMS (EUR base)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <div className="sm:col-span-2">
                                            <Field label="Partner">
                                                <PartnerAutocomplete
                                                    value={newForm.partner_name}
                                                    onChange={(v) => setNewForm({ ...newForm, partner_name: v })}
                                                    placeholder="Type to search partner…"
                                                    testId="ob-new-partner"
                                                />
                                            </Field>
                                        </div>
                                        <Field label="Currency">
                                            <Select value={newForm.currency} onValueChange={(v) => setNewForm({ ...newForm, currency: v })}>
                                                <SelectTrigger className="rounded-sm font-mono" data-testid="ob-new-currency"><SelectValue/></SelectTrigger>
                                                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </Field>
                                        <div></div>
                                        <Field label="Debit (Base)">
                                            <Input type="number" step="0.01" value={newForm.debit_base} onChange={(e) => setNewForm({ ...newForm, debit_base: e.target.value })} className="rounded-sm h-9 font-mono text-right" data-testid="ob-new-debit"/>
                                        </Field>
                                        <Field label="Credit (Base)">
                                            <Input type="number" step="0.01" value={newForm.credit_base} onChange={(e) => setNewForm({ ...newForm, credit_base: e.target.value })} className="rounded-sm h-9 font-mono text-right" data-testid="ob-new-credit"/>
                                        </Field>
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-1">
                                        If a row already exists for this <span className="font-medium">Entity + Segment + Partner</span>, it will be replaced.
                                    </div>
                                    <DialogFooter>
                                        <Button variant="ghost" onClick={() => { setNewOpen(false); setNewForm(blankNew()); }}>Cancel</Button>
                                        <Button onClick={submitNew} className="rounded-sm bg-[#002FA7]" data-testid="ob-new-submit">Save</Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        )}
                    </div>
                }
            />

            {canWrite && (
                <div className="csv-dropzone p-6 mb-6 flex items-center justify-between" data-testid="ob-uploader">
                    <div className="flex items-center gap-4">
                        <Upload size={28} className="text-slate-400" />
                        <div>
                            <div className="text-[13.5px] font-medium">Upload opening balances CSV</div>
                            <div className="text-[11.5px] text-slate-500">
                                Columns: Segment, <span className="font-medium">Entity_Name</span>, Partner_Name, <span className="font-medium">Billing_Cycle</span>, Currency, Debit_Base, Credit_Base
                            </div>
                            <div className="text-[10.5px] text-slate-400 mt-0.5">
                                Existing rows for the same <span className="font-medium">Entity + Segment + Partner</span> are replaced. Rows in the system but missing from the CSV are kept untouched.
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Select value={entityId} onValueChange={setEntityId}>
                            <SelectTrigger className="rounded-sm w-56" data-testid="ob-entity-select">
                                <SelectValue placeholder="Fallback entity (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                                {entities.map((e) => (
                                    <SelectItem key={e.id} value={e.id}>
                                        {e.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <input ref={fileRef} type="file" accept=".csv" onChange={onFile} hidden data-testid="ob-file-input" />
                        <Button
                            onClick={() => fileRef.current?.click()}
                            className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]"
                            data-testid="ob-upload-btn"
                        >
                            Choose CSV…
                        </Button>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="text-[11px] uppercase tracking-widest text-slate-500">Filter</span>
                <Select value={filterEntity} onValueChange={setFilterEntity}>
                    <SelectTrigger className="rounded-sm h-8 w-56" data-testid="ob-filter-entity">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Entities</SelectItem>
                        {entities.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                                {e.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by partner name…"
                        className="rounded-sm h-8 pl-7 w-64"
                        data-testid="ob-search-input"
                    />
                    {query && (
                        <button type="button" onClick={() => setQuery("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-[12px]"
                                aria-label="Clear search">×</button>
                    )}
                </div>
                <span className="text-[11px] text-slate-500" data-testid="ob-row-count">
                    {filteredRows.length === rows.length ? `${rows.length} row${rows.length === 1 ? "" : "s"}` : `${filteredRows.length} of ${rows.length} row${rows.length === 1 ? "" : "s"}`}
                </span>
            </div>

            <div className="bg-white border border-slate-200 overflow-hidden">
                <table className="w-full text-[13px] financial">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-4 py-2.5">Entity</th>
                            <th className="px-4 py-2.5">Segment</th>
                            <th className="px-4 py-2.5">Partner</th>
                            <th className="px-4 py-2.5">Currency</th>
                            <th className="px-4 py-2.5 num">Debit (Base)</th>
                            <th className="px-4 py-2.5 num">Credit (Base)</th>
                            <th className="px-4 py-2.5">Base</th>
                            <th className="px-4 py-2.5 w-24 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows.length === 0 && (
                            <tr>
                                <td colSpan={8} className="text-center text-slate-500 py-10">
                                    {rows.length === 0 ? "No opening balance rows yet" : `No partners match "${query}"`}
                                </td>
                            </tr>
                        )}
                        {filteredRows.map((r) => (
                            editId === r.id ? (
                                <tr key={r.id} className="border-b border-slate-100 bg-blue-50/40" data-testid={`ob-edit-row-${r.id}`}>
                                    <td className="px-4 py-2">
                                        <Select value={editForm.entity_id} onValueChange={(v) => setEditForm({ ...editForm, entity_id: v })}>
                                            <SelectTrigger className="h-8 rounded-sm" data-testid={`ob-edit-entity-${r.id}`}><SelectValue/></SelectTrigger>
                                            <SelectContent>{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-4 py-2">
                                        <Select value={editForm.segment} onValueChange={(v) => setEditForm({ ...editForm, segment: v })}>
                                            <SelectTrigger className="h-8 rounded-sm" data-testid={`ob-edit-segment-${r.id}`}><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Voice">Voice</SelectItem>
                                                <SelectItem value="SMS">SMS</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input value={editForm.partner_name} onChange={(e) => setEditForm({ ...editForm, partner_name: e.target.value })} className="h-8 rounded-sm" data-testid={`ob-edit-partner-${r.id}`}/>
                                    </td>
                                    <td className="px-4 py-2">
                                        <Select value={editForm.currency} onValueChange={(v) => setEditForm({ ...editForm, currency: v })}>
                                            <SelectTrigger className="h-8 rounded-sm font-mono" data-testid={`ob-edit-currency-${r.id}`}><SelectValue/></SelectTrigger>
                                            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-4 py-2 num">
                                        <Input type="number" step="0.01" value={editForm.debit_base} onChange={(e) => setEditForm({ ...editForm, debit_base: e.target.value })} className="h-8 rounded-sm font-mono text-right" data-testid={`ob-edit-debit-${r.id}`}/>
                                    </td>
                                    <td className="px-4 py-2 num">
                                        <Input type="number" step="0.01" value={editForm.credit_base} onChange={(e) => setEditForm({ ...editForm, credit_base: e.target.value })} className="h-8 rounded-sm font-mono text-right" data-testid={`ob-edit-credit-${r.id}`}/>
                                    </td>
                                    <td className="px-4 py-2 font-mono text-slate-500">{r.base_currency}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-right">
                                        <Button variant="ghost" size="sm" onClick={saveEdit} className="h-7 px-2 text-emerald-700" data-testid={`ob-save-${r.id}`}><Check size={13}/></Button>
                                        <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-7 px-2 ml-1" data-testid={`ob-cancel-${r.id}`}><XIcon size={13}/></Button>
                                    </td>
                                </tr>
                            ) : (
                                <tr key={r.id} className="border-b border-slate-100" data-testid={`ob-row-${r.id}`}>
                                    <td className="px-4 py-2">{entityName(r.entity_id)}</td>
                                    <td className="px-4 py-2">
                                        <SegBadge segment={r.segment} />
                                    </td>
                                    <td className="px-4 py-2">{r.partner_name}</td>
                                    <td className="px-4 py-2 font-mono">{r.currency}</td>
                                    <td className="px-4 py-2 num">{Number(r.debit_base).toFixed(2)}</td>
                                    <td className="px-4 py-2 num">{Number(r.credit_base).toFixed(2)}</td>
                                    <td className="px-4 py-2 font-mono text-slate-500">{r.base_currency}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-right">
                                        {canWrite && (
                                            <>
                                                <Button variant="ghost" size="sm" onClick={() => startEdit(r)} className="h-7 px-2" data-testid={`ob-edit-${r.id}`}><Pencil size={13}/></Button>
                                                <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="h-7 px-2 text-red-600" data-testid={`ob-delete-${r.id}`}><Trash2 size={13}/></Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            )
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function SegBadge({ segment }) {
    const cls =
        segment === "Voice"
            ? "bg-sky-100 text-sky-800 border-sky-200"
            : "bg-violet-100 text-violet-800 border-violet-200";
    return (
        <span className={`text-[11px] uppercase tracking-widest px-2 py-0.5 border ${cls}`}>{segment}</span>
    );
}
